import { Camera, MathUtils, Vector3 } from "three";
import {
  OrbitControls,
  type OrbitControlsEventMap,
} from "three/examples/jsm/controls/OrbitControls.js";
import { CollisionSystem } from "../collision/CollisionSystem";

const _scratchDesiredPosition = new Vector3();
const _scratchNextPosition = new Vector3();

export class OrbitCameraControls {
  minDistance: number = 0;
  maxDistance: number = Infinity;
  minPolarAngle: number = 0;
  maxPolarAngle: number = Math.PI;
  clampMinHeight: number = -Infinity;
  clampMaxHeight: number = Infinity;
  clampMaxHeightFar: number = Infinity;
  collisionSystem: CollisionSystem | null = null;

  private controls: OrbitControls;
  private lastSafePosition: Vector3 = new Vector3();

  constructor(object: Camera, domElement: HTMLElement | null = null) {
    this.controls = new OrbitControls(object, domElement ?? undefined);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.enablePan = true;
    this.controls.rotateSpeed = 0.6;
    this.controls.zoomSpeed = 0.8;
    this.controls.enabled = false;
    this.lastSafePosition.copy(object.position);
  }

  get enabled(): boolean {
    return this.controls.enabled;
  }

  set enabled(value: boolean) {
    this.controls.enabled = value;
  }

  get target(): Vector3 {
    return this.controls.target;
  }

  addEventListener<K extends keyof OrbitControlsEventMap>(
    type: K,
    listener: (event: OrbitControlsEventMap[K]) => void
  ): void {
    this.controls.addEventListener(type, listener);
  }

  removeEventListener<K extends keyof OrbitControlsEventMap>(
    type: K,
    listener: (event: OrbitControlsEventMap[K]) => void
  ): void {
    this.controls.removeEventListener(type, listener);
  }

  connect(element: HTMLElement): void {
    this.controls.connect(element);
  }

  disconnect(): void {
    this.controls.disconnect();
  }

  dispose(): void {
    this.controls.dispose();
  }

  setTarget(target: Vector3): void {
    this.controls.target.copy(target);
    this.controls.update();
    this.lastSafePosition.copy(this.controls.object.position);
  }

  applyLimits(): void {
    this.controls.minDistance = this.minDistance;
    this.controls.maxDistance = this.maxDistance;
    this.controls.minPolarAngle = this.minPolarAngle;
    this.controls.maxPolarAngle = this.maxPolarAngle;
  }

  syncPosition(): void {
    this.lastSafePosition.copy(this.controls.object.position);
  }

  update(): void {
    this.controls.update();
    this.applyConstraints();
  }

  rotateLeft(angle: number): boolean {
    const orbitControls =
      this.controls as OrbitControls & { rotateLeft?: (angle: number) => void };
    if (typeof orbitControls.rotateLeft !== "function") {
      return false;
    }
    orbitControls.rotateLeft.call(this.controls, angle);
    return true;
  }

  setAutoRotate(enabled: boolean, speed: number): void {
    this.controls.autoRotate = enabled;
    this.controls.autoRotateSpeed = speed;
  }

  private computeEffectiveMaxHeight(): number {
    if (this.clampMaxHeight === Infinity) {
      return Infinity;
    }

    if (this.clampMaxHeightFar >= this.clampMaxHeight || this.maxDistance <= this.minDistance) {
      return this.clampMaxHeight;
    }

    const distance = this.controls.object.position.distanceTo(this.controls.target);
    const t = MathUtils.clamp(
      (distance - this.minDistance) / (this.maxDistance - this.minDistance),
      0,
      1
    );

    return MathUtils.lerp(this.clampMaxHeight, this.clampMaxHeightFar, t);
  }

  private applyConstraints(): void {
    const desiredPosition = _scratchDesiredPosition.copy(this.controls.object.position);
    const constrainedPosition = _scratchNextPosition.copy(desiredPosition);

    if (this.collisionSystem) {
      constrainedPosition.copy(
        this.collisionSystem.getSafePosition(this.lastSafePosition, desiredPosition)
      );
    }

    const effectiveMaxHeight = this.computeEffectiveMaxHeight();

    if (this.clampMinHeight !== -Infinity || effectiveMaxHeight !== Infinity) {
      constrainedPosition.y = MathUtils.clamp(
        constrainedPosition.y,
        this.clampMinHeight,
        effectiveMaxHeight
      );
    }

    if (!constrainedPosition.equals(desiredPosition)) {
      this.controls.object.position.copy(constrainedPosition);
      this.controls.update();
    }

    this.lastSafePosition.copy(this.controls.object.position);
  }
}
